import React, { useEffect, useRef } from 'react';
import styles from './MoonshotCreateTeamForm.less';
import Select, { components } from 'react-select';
import _ from 'underscore';
import { useMoonshotCreateTeamQuery } from '@trello/graphql/generated';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary';
import { Team } from 'app/scripts/debug/constants';

interface CreateTeamProps {
  onChangeCategory: (opt: Option) => void;
  onChangeTeamName: React.ChangeEventHandler<HTMLInputElement>;
  teamName: string;
  teamNameError: string;
  categoryError: string;
}

interface CreateTeamFormComponentProps {
  readonly isFocused: boolean;
  readonly isSelected: boolean;
}

interface Option {
  value: string;
  label: string;
}

const items = _.shuffle<Option>([
  { value: 'it', label: 'IT' },
  { value: 'operations', label: 'Operations' },
  { value: 'sales', label: 'Sales' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'human resources', label: 'Human Resources' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'product', label: 'Product' },
  { value: 'design', label: 'Design' },
  { value: 'support', label: 'Support' },
]).concat({ value: 'other', label: 'Other' });

const Input: React.FC<CreateTeamFormComponentProps> = ({ ...props }) => (
  <components.Input {...props} className={styles.createTeamMoonshotDropdown} />
);

export const MoonshotCreateTeamForm: React.FC<CreateTeamProps> = ({
  categoryError,
  teamName,
  teamNameError,
  onChangeCategory,
  onChangeTeamName,
}) => {
  const teamInputRef = useRef<HTMLInputElement>(null);
  const { data } = useMoonshotCreateTeamQuery({
    variables: {
      memberId: 'me',
    },
  });

  const username = data?.member?.fullName || '';

  // Urgh, we want to auto focus the first input but the team picker from
  // Coffeescript land steals the focus, so we need to wait a little
  useEffect(() => {
    setTimeout(() => teamInputRef.current?.focus(), 100);
  }, [teamInputRef]);

  return (
    <ErrorBoundary tags={{ ownershipArea: Team.BizTeam }}>
      <div className={styles.moonshotCreateTeamFormContent}>
        <div className={styles.moonshotCreateTeamFormHeadline}>
          <h1>
            <span role="img" aria-label="waving hand">
              👋
            </span>{' '}
            Welcome, <strong>{username}!</strong>
          </h1>
        </div>
        <div className={styles.moonshotCreateTeamForm}>
          <label
            className={styles.moonshotCreateTeamLabel}
            htmlFor="moonshotCreateTeam"
          >
            What is Your Team Named?
            <span className={styles.createTeamLabelstar}>*</span>
          </label>
          <input
            id="moonshotCreateTeam"
            className={styles.moonshotCreateTeamInput}
            type="text"
            autoComplete="off"
            spellCheck={false}
            onChange={onChangeTeamName}
            autoFocus={true}
            placeholder="i.e., Pawnee Parks Department"
            value={teamName}
            maxLength={100}
            minLength={1}
            aria-describedby="moonshotCreateTeamNameError"
            ref={teamInputRef}
          />
          <div
            id="moonshotCreateTeamNameError"
            role="alert"
            aria-atomic
            className={styles.error}
          >
            {!!teamNameError && <p>{teamNameError}</p>}
          </div>
          <label
            className={styles.moonshotCreateTeamLabel}
            htmlFor="moonshotCreateTeamCategory"
          >
            What will you be using Trello for?
            <span className={styles.createTeamLabelstar}>*</span>
          </label>
          <Select
            blurInputOnSelect={true}
            className={styles.moonshotCreateTeamDropdown}
            placeholder="Choose something..."
            options={items}
            inputId="moonshotCreateTeamCategory"
            aria-describedby="moonshotCreateTeamCategoryError"
            onChange={onChangeCategory}
            components={{ Input, IndicatorSeparator: () => null }}
          />
          <div
            id="moonshotCreateTeamCategoryError"
            role="alert"
            aria-atomic
            className={styles.error}
          >
            {!!categoryError && <p>{categoryError}</p>}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};
